/**
 * ÅRGANGEN FESTIVALSIDEN VISER: DEN BRUKEREN SIST VAR PÅ (sak 217.13, K-18).
 *
 * Før viste siden alltid årgangen med høyest år, så en ny «2028» byttet oversikten for alle. Nå
 * huskes den siste årgangen hver person var inne i, per festival, på serveren — visningsvalget
 * (`/v1/me/view-prefs`), ikke nettleseren — så den overlever en oppdatering og en ny maskin.
 *
 * Verdien er ett objekt per hus: `{ [festivalId]: editionId }`. Én rad per hus og ikke per
 * festival, fordi nøkkelen er `<visning>:<hus-id>` (kontrakten). Kontrakten tåler 40 nøkler; ved
 * flere festivaler enn det glemmes den eldste, som er den riktige å glemme.
 *
 * NYE BRUKERE, og den som sist var i en årgang som er slettet, får den PÅGÅENDE årgangen, ellers
 * den NÆRMESTE KOMMENDE, ellers den nyeste.
 */
import { MAX_VIEW_PREF_KEYS, type EditionDto } from '@tvitvi/shared';
import { useRouterState } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';

import { useViewPrefs, viewKeyFor } from '@/api/queries/view-prefs';

/** `/festivals/<id>/editions/<id>` og alt under den. Adressen er fasit på hvor man er. */
const EDITION_PATH = /^\/festivals\/([0-9a-f-]{36})\/editions\/([0-9a-f-]{36})(?:\/|$)/;

/** «I dag» som `YYYY-MM-DD` i leserens egen kalender — samme form som `startsOn`/`endsOn`. */
function isoToday(today: Date): string {
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${String(today.getFullYear())}-${month}-${day}`;
}

/**
 * Pågående, ellers nærmeste kommende, ellers den første i lista (API-et sorterer nyeste først).
 */
export function defaultEdition(
  editions: readonly EditionDto[],
  today: Date = new Date(),
): EditionDto | null {
  const now = isoToday(today);
  const ongoing = editions.find(
    (edition) =>
      edition.startsOn !== null &&
      edition.startsOn <= now &&
      (edition.endsOn ?? edition.startsOn) >= now,
  );
  if (ongoing !== undefined) return ongoing;

  const upcoming = editions
    .filter((edition) => edition.startsOn !== null && edition.startsOn > now)
    .sort((a, b) => (a.startsOn ?? '').localeCompare(b.startsOn ?? ''));
  return upcoming[0] ?? editions[0] ?? null;
}

/** Den husket årgangen hvis den fortsatt finnes, ellers standarden over. */
export function pickEdition(
  editions: readonly EditionDto[],
  remembered: unknown,
  today?: Date,
): EditionDto | null {
  if (typeof remembered === 'string') {
    const found = editions.find((edition) => edition.id === remembered);
    if (found !== undefined) return found;
  }
  return defaultEdition(editions, today);
}

export interface LastEdition {
  /** Den husket årgangens id for festivalen, eller `undefined`. */
  remembered: (festivalId: string) => unknown;
  /** Om svaret fra serveren har kommet. Før det vet siden ikke hva brukeren så sist. */
  ready: boolean;
}

export function useLastEdition(organizationId: string | null): LastEdition {
  const prefs = useViewPrefs(viewKeyFor('festival', organizationId ?? ''), organizationId !== null);
  return {
    remembered: (festivalId) => prefs.value[festivalId],
    ready: organizationId !== null && prefs.ready,
  };
}

/**
 * Skriver ned årgangen man står i, hvor i årgangen enn man er. Monteres ÉN gang, i skallet, så
 * hver side under en årgang teller uten at hver side må vite om det.
 */
export function useRememberLastEdition(organizationId: string | null): void {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const prefs = useViewPrefs(viewKeyFor('festival', organizationId ?? ''), organizationId !== null);
  const { ready, value, save } = prefs;

  const match = EDITION_PATH.exec(pathname);
  const festivalId = match?.[1];
  const editionId = match?.[2];

  /*
   * ÉTT FORSØK PER ÅRGANG, samme mønster som `lastSaved` i kalenderen og oppgavene (3. oktober).
   * Uten den gikk en mislykket lagring i løkke: `onError` legger den gamle verdien tilbake, effekten
   * ser forskjellen og lagrer igjen — målt til 9 000 PUT på 30 sekunder mot testmiljøet på nett.
   */
  const attempted = useRef<string | null>(null);

  useEffect(() => {
    // Aldri skriv før raden er lest: da ville en tom standard lagt seg over det som sto der.
    if (organizationId === null || !ready) return;
    if (festivalId === undefined || editionId === undefined) return;
    if (value[festivalId] === editionId) return;
    const pair = `${organizationId}:${festivalId}:${editionId}`;
    if (attempted.current === pair) return;
    attempted.current = pair;

    const rest = Object.entries(value).filter(([key]) => key !== festivalId);
    const kept = rest.slice(Math.max(0, rest.length - (MAX_VIEW_PREF_KEYS - 1)));
    save({ ...Object.fromEntries(kept), [festivalId]: editionId });
  }, [organizationId, ready, value, save, festivalId, editionId]);
}
